import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  RiBugLine,
  RiLightbulbLine,
  RiQuestionLine,
  RiExternalLinkLine,
  RiImageLine,
  RiCloseLine,
} from '@remixicon/react'
import {
  Alert,
  Badge,
  Card,
  Drawer,
  EmptyState,
  Field,
  PageHeader,
  SectionSpinner,
  Select,
} from '../components/ui'
import { useMediaQuery } from '../hooks'
import { api } from '../utils/api'
import { toErrorMessage } from '../lib/errors'
import { formatDateTime } from '../lib/dates'
import { FEEDBACK_STATUS_TONE } from '../lib/status'
import { cx, focusRing, neutralTile } from '../lib/utils'

interface FeedbackMetadata {
  url: string
  userAgent: string
  screenSize: string
  viewportSize: string
  timestamp: string
}

type FeedbackType = 'bug' | 'feature' | 'question'
type FeedbackStatus = 'open' | 'in_progress' | 'resolved' | 'closed'

interface Feedback {
  id: string
  user_id: string
  user_email: string
  user_name: string
  type: FeedbackType
  title: string
  description: string | null
  screenshot: string | null
  metadata: FeedbackMetadata | null
  status: FeedbackStatus
  github_issue_url: string | null
  created_at: string
  updated_at: string | null
}

/**
 * The three kinds of report, in the same three hues the rest of the console
 * uses for its own kinds. They are deliberately not the status colours: a
 * report's type and its state are different questions, and answering both in
 * emerald and amber made neither readable.
 */
const TYPE_STYLE: Record<FeedbackType, { icon: typeof RiBugLine; classes: string }> = {
  bug: {
    icon: RiBugLine,
    classes:
      neutralTile,
  },
  feature: {
    icon: RiLightbulbLine,
    classes:
      neutralTile,
  },
  question: {
    icon: RiQuestionLine,
    classes:
      'bg-primary-50 text-primary-600 ring-primary-100 dark:bg-primary-500/10 dark:text-primary-400 dark:ring-primary-500/20',
  },
}

/**
 * Written out rather than built from the value. The keys used to be assembled
 * with `charAt(0).toUpperCase()` and a `replace('_','')`, which is a lot of
 * string surgery standing between a status and its label — and it fails
 * silently, showing the key, the day someone adds a status with two
 * underscores.
 */
const STATUS_KEY: Record<FeedbackStatus, string> = {
  open: 'adminFeedback.statusOpen',
  in_progress: 'adminFeedback.statusInProgress',
  resolved: 'adminFeedback.statusResolved',
  closed: 'adminFeedback.statusClosed',
}

const TYPE_KEY: Record<FeedbackType, string> = {
  bug: 'feedback.typeBug',
  feature: 'feedback.typeFeature',
  question: 'feedback.typeQuestion',
}

const STATUS_ORDER: FeedbackStatus[] = ['open', 'in_progress', 'resolved', 'closed']

/** Tailwind's `lg`. Kept as a constant so the query is one literal string. */
const SIDE_PANEL_QUERY = '(min-width: 1024px)'

export default function AdminFeedback() {
  const { t, i18n } = useTranslation()

  const [feedbackList, setFeedbackList] = useState<Feedback[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [showScreenshot, setShowScreenshot] = useState(false)

  // Decided in state rather than with `lg:hidden`, because the drawer renders
  // into a portal: hiding its wrapper hides nothing, and the drawer opened
  // over the side panel on a desktop as well.
  const hasSidePanel = useMediaQuery(SIDE_PANEL_QUERY)

  // Read from the list rather than kept as its own copy: a status change
  // reloads the list, and a stale snapshot would keep showing the old state
  // in the panel that was used to change it.
  const selected = feedbackList.find((entry) => entry.id === selectedId) ?? null

  const loadFeedback = useCallback(async () => {
    try {
      const response = await api.get<{ feedback: Feedback[] }>('/api/feedback')
      setFeedbackList(response.feedback)
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setIsLoading(false)
    }
  }, [t])

  useEffect(() => {
    loadFeedback()
  }, [loadFeedback])

  const updateStatus = async (id: string, status: string) => {
    setError(null)
    try {
      await api.put(`/api/feedback/${id}/status`, { status })
      await loadFeedback()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    }
  }

  const select = (entry: Feedback) => {
    setSelectedId(entry.id)
    // Below the breakpoint the detail used to sit under the whole list, so
    // tapping a report scrolled nothing into view and looked like nothing
    // had happened. There it opens as a drawer instead.
    if (!hasSidePanel) setDetailOpen(true)
  }

  return (
    <>
      <PageHeader title={t('adminFeedback.title')} subtitle={t('adminFeedback.subtitle')} />

      {error && <Alert className="mb-6">{error}</Alert>}

      {isLoading ? (
        <SectionSpinner />
      ) : feedbackList.length === 0 ? (
        <Card>
          <EmptyState
            icon={<RiBugLine className="size-6" aria-hidden="true" />}
            title={t('adminFeedback.noFeedback')}
            description={t('adminFeedback.noFeedbackDescription')}
          />
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* min-w-0: a grid item defaults to `min-width: auto` and refuses
              to shrink below its content, so a long report title pushed the
              whole card past the screen on a phone. */}
          <Card className="min-w-0 divide-y divide-gray-100 lg:col-span-2 dark:divide-gray-800">
            {feedbackList.map((entry) => {
              const style = TYPE_STYLE[entry.type]
              const Icon = style.icon

              return (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => select(entry)}
                  aria-pressed={selectedId === entry.id}
                  className={cx(
                    'flex w-full items-start gap-4 p-4 text-left transition',
                    'hover:bg-gray-50 dark:hover:bg-gray-900',
                    'aria-pressed:bg-primary-50/60 dark:aria-pressed:bg-primary-500/10',
                    focusRing
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cx(
                      'flex size-9 shrink-0 items-center justify-center rounded-lg ring-1',
                      style.classes
                    )}
                  >
                    <Icon className="size-5" />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="min-w-0 truncate font-semibold text-gray-950 dark:text-white">
                        {entry.title}
                      </span>
                      {entry.screenshot && (
                        <RiImageLine
                          className="size-4 shrink-0 text-gray-400"
                          aria-label={t('adminFeedback.hasScreenshot')}
                          role="img"
                        />
                      )}
                      <Badge variant={FEEDBACK_STATUS_TONE[entry.status]} size="sm">
                        {t(STATUS_KEY[entry.status])}
                      </Badge>
                    </span>

                    <span className="mt-0.5 block truncate text-sm text-gray-500 dark:text-gray-400">
                      {entry.user_name || entry.user_email}
                      {` · ${formatDateTime(entry.created_at, i18n.language)}`}
                    </span>

                    {entry.description && (
                      <span className="mt-2 line-clamp-2 block text-sm text-gray-600 dark:text-gray-300">
                        {entry.description}
                      </span>
                    )}
                  </span>
                </button>
              )
            })}
          </Card>

          {/* Beside the list where there is room for it, a drawer where there
              is not. Only one of the two ever mounts. */}
          {hasSidePanel ? (
            <div className="min-w-0 lg:col-span-1">
              {selected ? (
                <Card className="sticky top-20 p-6">
                  <FeedbackDetail
                    entry={selected}
                    onStatusChange={updateStatus}
                    onShowScreenshot={() => setShowScreenshot(true)}
                  />
                </Card>
              ) : (
                <Card className="p-6">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('adminFeedback.selectFeedback')}
                  </p>
                </Card>
              )}
            </div>
          ) : (
            <Drawer
              open={detailOpen && selected !== null}
              onClose={() => setDetailOpen(false)}
              title={selected?.title ?? ''}
              subtitle={selected ? t(TYPE_KEY[selected.type]) : undefined}
              size="lg"
            >
              {selected && (
                <FeedbackDetail
                  entry={selected}
                  hideTitle
                  onStatusChange={updateStatus}
                  onShowScreenshot={() => setShowScreenshot(true)}
                />
              )}
            </Drawer>
          )}
        </div>
      )}

      {showScreenshot && selected?.screenshot && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setShowScreenshot(false)}
        >
          <button
            type="button"
            onClick={() => setShowScreenshot(false)}
            aria-label={t('common.close')}
            className={cx('absolute right-4 top-4 text-white hover:text-gray-300', focusRing)}
          >
            <RiCloseLine className="size-8" aria-hidden="true" />
          </button>
          <img
            src={selected.screenshot}
            alt={t('feedback.screenshot')}
            className="max-h-[90vh] max-w-full rounded-lg shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </>
  )
}

interface FeedbackDetailProps {
  entry: Feedback
  /** The drawer already shows the title in its header. */
  hideTitle?: boolean
  onStatusChange: (id: string, status: string) => void
  onShowScreenshot: () => void
}

/**
 * One report in full. Shared by the side panel and the drawer so the two can
 * never drift into showing different things about the same report.
 */
function FeedbackDetail({
  entry,
  hideTitle,
  onStatusChange,
  onShowScreenshot,
}: FeedbackDetailProps) {
  const { t, i18n } = useTranslation()
  const style = TYPE_STYLE[entry.type]
  const Icon = style.icon

  return (
    <div className="space-y-5">
      {!hideTitle && (
        <div>
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className={cx(
                'flex size-7 shrink-0 items-center justify-center rounded-md ring-1',
                style.classes
              )}
            >
              <Icon className="size-4" />
            </span>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {t(TYPE_KEY[entry.type])}
            </span>
          </div>
          <h2 className="mt-2 text-display-sm text-gray-950 dark:text-white">{entry.title}</h2>
        </div>
      )}

      <Field label={t('common.status')}>
        <Select
          value={entry.status}
          onChange={(event) => onStatusChange(entry.id, event.target.value)}
          options={STATUS_ORDER.map((status) => ({
            value: status,
            label: t(STATUS_KEY[status]),
          }))}
        />
      </Field>

      {entry.description && (
        <Detail label={t('feedback.descriptionLabel')}>
          <p className="whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-300">
            {entry.description}
          </p>
        </Detail>
      )}

      <Detail label={t('adminFeedback.reportedBy')}>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {entry.user_name || '—'}
          <br />
          <span className="text-gray-500 dark:text-gray-400">{entry.user_email}</span>
        </p>
      </Detail>

      <Detail label={t('common.createdAt')}>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {formatDateTime(entry.created_at, i18n.language)}
        </p>
      </Detail>

      {entry.metadata && (
        <Detail label={t('adminFeedback.metadata')}>
          <dl className="space-y-1 rounded-md bg-gray-50 p-3 text-xs dark:bg-gray-900">
            {[
              ['URL', entry.metadata.url],
              [t('adminFeedback.screen'), entry.metadata.screenSize],
              [t('adminFeedback.viewport'), entry.metadata.viewportSize],
            ].map(([label, value]) => (
              <div key={label} className="flex gap-2">
                <dt className="shrink-0 font-medium text-gray-500 dark:text-gray-400">{label}</dt>
                <dd className="min-w-0 break-all text-gray-700 dark:text-gray-300">{value}</dd>
              </div>
            ))}
          </dl>
        </Detail>
      )}

      {entry.screenshot && (
        <Detail label={t('feedback.screenshot')}>
          <button
            type="button"
            onClick={onShowScreenshot}
            className={cx('block w-full rounded-md', focusRing)}
          >
            <img
              src={entry.screenshot}
              alt={t('feedback.screenshot')}
              className="w-full cursor-zoom-in rounded-md border border-gray-200 transition hover:opacity-90 dark:border-gray-800"
            />
          </button>
        </Detail>
      )}

      {entry.github_issue_url && (
        <a
          href={entry.github_issue_url}
          target="_blank"
          rel="noopener noreferrer"
          className={cx(
            'inline-flex items-center gap-2 rounded-md text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400',
            focusRing
          )}
        >
          <RiExternalLinkLine className="size-4" aria-hidden="true" />
          {t('adminFeedback.viewOnGitHub')}
        </a>
      )}
    </div>
  )
}

/**
 * A labelled block of read-only text — the page had this spelled out eight
 * times. Not the shared `Field`, which exists to wire a label to a form
 * control and hands its child a generated id; there is no control here to
 * point a label at, and a <label> for nothing is a lie to a screen reader.
 */
function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
      {children}
    </div>
  )
}
